<template>
  <WizardBox title="External Supplier Setup">
    <template v-slot:content>
      <FormGroup v-bind:form="orderForm">
        <FormWizard ref="orderWizard" :submitting="submitting" v-bind:tabList="tabList" @finished="handleSubmit()" @stepChange="wizardStepChanged">
          <template v-slot:content>
            
            <FormWizardStep index="1" v-bind:active="true">

              <OrderItemList
                ref="orderItemList"
                v-model="itemList"
                :editing="true"
                :isDespatchDelivery="false"
                :orderType="order.type"
                :showTotals="false"
                :priceType="PriceTypes.supplier"
                :supplierLookupList="supplierLookupList"

                :optionSupplierDetailsAvailable="true"
                :optionDiscountAvailable="false"
                :optionCustomFieldsAvailable="false"
                :optionCustomerApprovalAvailable="false"
                :optionExternalAvailable="false"
                :optionAddServiceAvailable="false"
                :optionBlockRemovingExternal="true"
                :optionNewItemGlassAvailable="false"
                :optionNewItemGeneralAvailable="false"
                :optionNewItemTemplateAvailable="false"
                :optionNewItemAssemblyAvailable="false"
              />
            </FormWizardStep>

            <FormWizardStep index="2">
              <div class="row">
                <div class="col-12">
                  <FormRow label="Required Date">
                    <FormDate v-model="despatchInfo.dateRequired" field="dateRequired" />
                  </FormRow>
                  <FormHorizontalRule />
                  <FormRow label="Despatch Method">
                    <FormOption label="Delivery" name="despatchType" :setting="DespatchTypes.delivery" v-model="despatchInfo.despatchType" inline="true" />
                    <FormOption label="Pickup" name="despatchType" :setting="DespatchTypes.pickup" v-model="despatchInfo.despatchType" inline="true" />
                  </FormRow>
                  <div v-show="isDespatchDelivery">
                    <FormRow label="Delivery Location">
                      <FormSelect :options="accountLocationLookup" v-model="despatchInfo.deliveryAccountLocationId" field="deliveryAccountLocationId" />
                    </FormRow>

                    <FormRow label="Delivery Address 1">
                      <FormInputAddressSearch
                        placeholder="Address 1"
                        v-model="despatchInfo.deliveryAddress1"
                        field="deliveryAddress1"
                        :readonly="!isAddressEditable"
                        @updateAddress2="despatchInfo.deliveryAddress2 = $event"
                        @updateSuburb="despatchInfo.deliverySuburb = $event"
                        @updatePostcode="despatchInfo.deliveryPostcode = $event"
                        @updateState="despatchInfo.deliveryState = $event"
                        @updateCountry="despatchInfo.deliveryCountry = $event"
                      />

                    </FormRow>
                    <FormRow label="Delivery Address 2">
                      <FormInputText placeholder="Address 2" v-model="despatchInfo.deliveryAddress2" field="deliveryAddress2" :readonly="!isAddressEditable" />
                    </FormRow>
                    <FormRow label="Delivery Suburb">
                      <FormInputText placeholder="Suburb" v-model="despatchInfo.deliverySuburb" field="deliverySuburb" :readonly="!isAddressEditable" />
                    </FormRow>
                    <FormRow label="Delivery Postcode">
                      <FormInputText placeholder="Postcode" v-model="despatchInfo.deliveryPostcode" field="deliveryPostcode" :readonly="!isAddressEditable" />
                    </FormRow>
                    <FormRow label="Delivery State">
                      <FormInputText placeholder="State" v-model="despatchInfo.deliveryState" field="deliveryState" :readonly="!isAddressEditable" />
                    </FormRow>
                    <FormRow label="Delivery Country">
                      <FormInputText placeholder="Country" v-model="despatchInfo.deliveryCountry" field="deliveryCountry" :readonly="!isAddressEditable" />
                    </FormRow>
                  </div>
                </div>
              </div>

            </FormWizardStep>

            <FormWizardStep index="3">
              <p class="font-italic text-center" v-if="!isSuppliersSelected">No suppliers selected for external items</p>
              <div class="row review-data" v-if="isSuppliersSelected">
                <div class="col-9">
                  <div class="row">
                    <div class="col-6">
                      <div class="review-header">
                        <h2>External Supplier Summary</h2> <a class="review-edit-button" @click="displayDetailsTab"><i class="far fa-pencil"></i></a>
                      </div>
                      <FormRow label="Order" :labelColumnSize="5">
                        <FormStaticLabel><LinkOrder :id="order.id" :name="order.sequenceNumber || 'Order'" /></FormStaticLabel>
                      </FormRow>
                      <FormRow label="Despatch" :labelColumnSize="5">
                        <FormStaticLabel>
                          {{isDespatchDelivery ? 'Delivery' : ''}}
                          {{isDespatchPickup ? 'Pickup' : ''}}
                          <div v-if="isDespatchDelivery">Requested delivery date <span class="font-weight-bold">{{despatchInfo.dateRequired | formatDate}}</span></div>
                          <div v-if="isDespatchPickup">Requested pickup date <span class="font-weight-bold">{{despatchInfo.dateRequired | formatDate}}</span></div>
                          <p v-if="!isDespatchDelivery && !isDespatchPickup" class="text-danger">None selected</p>
                        </FormStaticLabel>
                      </FormRow>
                    </div>
                    <div class="col-6" v-if="isDespatchDelivery">
                      <div class="review-header">
                        <h2>Delivered To</h2> <a class="review-edit-button" @click="displayDespatchTab"><i class="far fa-pencil"></i></a>
                      </div>
                      <AddressDisplay
                        :address1="despatchInfo.deliveryAddress1"
                        :address2="despatchInfo.deliveryAddress2"
                        :suburb="despatchInfo.deliverySuburb"
                        :state="despatchInfo.deliveryState"
                        :postcode="despatchInfo.deliveryPostcode"
                        :country="despatchInfo.deliveryCountry"
                        />
                    </div>
                    <div class="col-6" v-if="isDespatchPickup">
                      <div class="review-header">
                        <h2>Pickup</h2> <a class="review-edit-button" @click="displayDespatchTab"><i class="far fa-pencil"></i></a>
                      </div>
                      -
                    </div>
                  </div>
                </div>
                <div class="col-3">
                  <div class="review-header">
                    <h2>Pricing (ex tax)</h2>
                  </div>
                  <div class="review-totals">
                    <div class="row"><div class="col-6">Order Total</div><div class="col-6 text-right">{{orderTotal | formatCurrency}}</div></div>
                    <div :class="{'row': true, 'status-credit-danger': purchaseOrderCostWarning}">
                      <div class="col-6">Estimated PO Total</div><div class="col-6 text-right">{{purchaseOrderEstimateTotal | formatCurrency}}</div></div>
                  </div>
                </div>
              </div>

              <div class="row review-data" v-if="isSuppliersSelected">
                <div class="col-9">
                  <div class="review-header review-header-seperator">
                    <h2>Purchase Order List</h2> <a class="review-edit-button" @click="displayDetailsTab"><i class="far fa-pencil"></i></a>
                  </div>

                  <div v-for="(poInfo, index) of supplierPurchaseOrderList" :key="poInfo.contactId">
                    <h3>Purchase Order {{index+1}} <span class="mx-3">&bull;</span> <LinkContact :id="poInfo.contactId" :name="poInfo.name" /></h3>
                    <div class="mb-4">
                      <OrderItemList
                        ref="reviewOrderItemList"
                        :value="poInfo.itemList"
                        :editing="false"
                        :isDespatchDelivery="false"
                        :showTotals="false"
                        :priceType="PriceTypes.supplier"
                        :supplierLookupList="supplierLookupList"

                        :optionSupplierDetailsAvailable="true"
                        :optionDiscountAvailable="false"
                        :optionCustomFieldsAvailable="false"
                        :optionCustomerApprovalAvailable="false"
                        :optionExternalAvailable="false"
                        :optionAddServiceAvailable="false"
                        :optionBlockRemovingExternal="true"
                        :optionNewItemGlassAvailable="false"
                        :optionNewItemGeneralAvailable="false"
                        :optionNewItemTemplateAvailable="false"
                        :optionNewItemAssemblyAvailable="false"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="row review-data" v-if="isSuppliersSelected && purchaseOrdersHaveNotices">
                <div class="col-9">
                  <div class="review-header review-header-seperator">
                    <h2>Notices</h2>
                  </div>
                  <p v-if="pricingWarningCount > 0" class="mb-1">
                    <Icon iconStyle="fas" icon="fa-usd-circle" iconClass="order-item-icon-price-error" iconStack="fa-circle" iconStackClass="order-item-icon-price-error-bg" class="mr-1"></Icon>
                    Purchase orders have {{pricingWarningCount}} item{{pricingWarningCount | displayCountPlural}} with <strong>price errors</strong>
                  </p>
                  <p v-if="missingSupplierWarningCount > 0" class="mb-1">
                    <Icon iconStyle="far" icon="fa-external-link-square-alt" iconClass="order-item-icon-external" class="mr-1" fixedWidth></Icon>
                    Purchase orders have {{missingSupplierWarningCount}} item{{missingSupplierWarningCount | displayCountPlural}} with no <strong>external supplier</strong> set
                  </p>
                  <p v-if="purchaseOrderCostWarning" class="mb-1">
                    <Icon iconStyle="far" icon="fa-credit-card" iconClass="status-credit-danger"  class="mr-1" fixedWidth></Icon>
                    Estimated <strong>purchase order total</strong> is greater than the <strong>order total</strong>
                  </p>
                </div>
              </div>

            </FormWizardStep>
          </template>
        </FormWizard>
      </FormGroup>
    </template>
  </WizardBox>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import form from '@/mixins/form'
import WizardBox from '@/components/wizardBox'

import Utils from '@/utils/utils'
import orderItemMixin from '@/modules/orders/mixins/orderItemMixin'
import OrderItemList from '@/modules/orders/components/orderItemList'
import { OrderTypes, PriceTypes, MessageTemplateTypes, InventoryStockTypes, InventoryStockTypeLookup, DespatchTypes } from '@/store/types'

export default {
  name: 'OrderExternalSupplierSetup',
  mixins: [
    form,
    orderItemMixin
  ],
  components: {
    WizardBox,
    OrderItemList
  },
  props: {
  },
  data() {
    return {
      orderForm: {},
      order: {},
      itemList: [],
      supplierLookupList: [],
      supplierPurchaseOrderList: [],
      submitting: false,
      loading: false,
      tabList: [
        { title: 'Items' },
        { title: 'Despatch' },
        { title: 'Review' },
      ],
      messageTemplateUpdateFn: this.getMessageTemplateUpdatePromise,
      accountData: {},
      accountLocationLookup: [],
      despatchInfo: {
        despatchType: DespatchTypes.delivery,
        dateRequired: moment().local().add(1, 'days').toDate().toISOString(),
        accountLocationId: null,
        deliveryAccountLocationId: null,
        deliveryAddress1: '',
        deliveryAddress2: '',
        deliverySuburb: '',
        deliveryState: '',
        deliveryPostcode: '',
        deliveryCountry: '',
      }
    }
  },
  watch: {
    'despatchInfo.deliveryAccountLocationId': function(newVal, oldVal) {
      // Fill in the delivery location details
      if(this.accountData.accountLocations) {
        var locationInfo = this.accountData.accountLocations.find(location => location.id == newVal)
        if(locationInfo) {
          this.despatchInfo.deliveryAddress1 = locationInfo.address1
          this.despatchInfo.deliveryAddress2 = locationInfo.address2
          this.despatchInfo.deliverySuburb = locationInfo.suburb
          this.despatchInfo.deliveryState = locationInfo.state
          this.despatchInfo.deliveryPostcode = locationInfo.postcode
          this.despatchInfo.deliveryCountry = locationInfo.country
        }
      }
    }
  },
  computed: {
    externalItems() {
      return this.itemList.filter(item => item.isExternal)
    },
    isDespatchDelivery() {
      return this.despatchInfo.despatchType == DespatchTypes.delivery
    },
    isDespatchPickup() {
      return this.despatchInfo.despatchType == DespatchTypes.pickup
    },
    isSuppliersSelected() {
      return this.supplierPurchaseOrderList.length > 0
    },
    isAddressEditable() {
      return !this.despatchInfo.deliveryAccountLocationId    // Only if custom address selected
    },
    orderTotal() {
      return this.order.totalPrice - this.order.totalTax
    },
    purchaseOrderEstimateTotal() {
      let totalEstimatedCost = this.supplierPurchaseOrderList.reduce((curCost, po) => {
        return curCost + po.estimatedCost
      }, 0)
      return Utils.roundCurrency(totalEstimatedCost)
    },
    purchaseOrderCostWarning() {
      return this.purchaseOrderEstimateTotal > this.orderTotal
    },
    pricingWarningCount() {
      return this.itemList.filter(item => item.isExternal && item.priceError != '').length
    },
    missingSupplierWarningCount() {
      return this.itemList.filter(item => item.isExternal && !item.supplierContactId).length
    },
    purchaseOrdersHaveNotices() {
      return this.purchaseOrderCostWarning || this.pricingWarningCount > 0 || this.missingSupplierWarningCount > 0
    }
  },
  created() {
    this.PriceTypes = PriceTypes
    this.MessageTemplateTypes = MessageTemplateTypes
    this.InventoryStockTypes = InventoryStockTypes
    this.InventoryStockTypeLookup = InventoryStockTypeLookup
    this.DespatchTypes = DespatchTypes
  },
  mounted() {
    // Load the order
    this.requestData()
  },
  methods: {
    requestData() {
      this.loading = true

      Promise.all([
        this.$refs.orderItemList.requestData(),
        this.$apiService.ordersService.getOrder(this.$route.params.id),
        this.$apiService.contactService.getContactList({ isActive: 1, isSupplier: 1 }),
        this.$apiService.adminService.getAccount()
      ])
      .then(results => {
        this.order = results[1].data

        this.despatchInfo.accountLocationId = this.order.accountLocationId
        this.despatchInfo.deliveryAccountLocationId = this.order.accountLocationId

        // Copy the order item list & modify for display/editing
        // Setup for supplier updating
        var items = this.buildOrderItemList(this.order.orderItems)
        this.itemList = items.map(orderItem => {
          orderItem.supplierContactId = null
          orderItem.supplierStockPriceGroupId = null
          orderItem.originalOrderItemId = orderItem.id
          orderItem.originalGlassWidth = orderItem.glassWidth
          orderItem.originalGlassHeight = orderItem.glassHeight
          orderItem.isGlassSizeLocked = true
          orderItem.isQuantityLocked = true
          orderItem.isStockLocked = true
          orderItem.isPriceGroupLocked = true

          // Clear out pricing
          orderItem.unitPrice = 0
          orderItem.itemPrice = 0
          orderItem.linePrice = 0

          orderItem.minChargePrice = 0
          orderItem.minChargeItemPrice = 0
          orderItem.minChargeQuantity = 0
          orderItem.minChargeInvoice = 0

          // Reset notes & use it for supplier notes
          orderItem.notes = ''

          orderItem.priceError = ''
          orderItem.productionError = ''
          orderItem.priceNotice = ''
          orderItem.unitsNotice = ''

          return orderItem
        })

        // Filter out groups that have no isExternal flags at all 
        this.itemList = this.itemList.filter(item => {
          // Filter out cancelled items
          if(item.isCancelled)
            return false

          if(item.isExternal)
            return true

          // Filter out special items used in order entry (min charge / invoice items)
          if(item.isMinInvoiceItem || item.isMinPriceItem || item.isMinPriceSingleItem)
            return false
          
          // If not external & no items in the same group are external then ignore it
          let rootItem = this.findRootOrderItemForParentId(this.itemList, item.id) || item
          let externalCount = this.countExternalItems(rootItem.id, this.itemList)

          // Ignore if the root item is cancelled
          if(rootItem.isCancelled)
            return false

          return externalCount > 0
        })

        this.$data.supplierLookupList = results[2].data
        .map(contact => {
          return {
            value: contact.id,
            name: contact.name
          }
        })

        this.accountData = results[3].data
        this.accountLocationLookup = [{ value: null, name: 'Custom' }]
        this.accountLocationLookup = _.concat(this.accountLocationLookup, this.accountData.accountLocations.map(location => {
          return { value: location.id, name: location.name }
        }))
      })
      .finally(() => {
        this.loading = false
      })
    },
    handleSubmit() {
      this.submitting = true

      this.rebuildPurchaseOrderList()

      var purchaseOrderList = this.supplierPurchaseOrderList.map(purchaseOrder => {
        return {
          contactId: purchaseOrder.contactId,
          itemList: this.buildNestedItemList(purchaseOrder.itemList),
        }
      })

      var setupData = {
        purchaseOrderList: purchaseOrderList,
        despatchType: this.despatchInfo.despatchType,
        dateRequired: this.despatchInfo.dateRequired,
        accountLocationId: this.despatchInfo.accountLocationId,
        deliveryAccountLocationId: this.despatchInfo.deliveryAccountLocationId,
        deliveryAddress1: this.despatchInfo.deliveryAddress1,
        deliveryAddress2: this.despatchInfo.deliveryAddress2,
        deliverySuburb: this.despatchInfo.deliverySuburb,
        deliveryState: this.despatchInfo.deliveryState,
        deliveryPostcode: this.despatchInfo.deliveryPostcode,
        deliveryCountry: this.despatchInfo.deliveryCountry,
      }

      this.$apiService.ordersService.setupExternalOrderItems(this.$route.params.id, setupData, { useCompression: true })
      .then(response => {
        this.submitting = false

        this.successToast('Order Updated')
        this.$router.push(`/orders/${this.$route.params.id}`)
      })
      .catch(err => {
        // Process errors
        this.$data.orderForm = this.processFormResponse(err)
        this.$refs.orderWizard.displayErrors()

        this.submitting = false
      })

    },
    countExternalItems(parentItemId, itemList) {
      var externalCount = 0

      itemList.forEach(item => {
        if(item.parentOrderItemId == parentItemId) {
          if(item.isExternal)
            externalCount++

          externalCount += this.countExternalItems(item.id, itemList)
        }
      })

      return externalCount
    },
    buildNestedItemList(itemList, parentOrderItemId = null) {
      return itemList.filter(item => item.parentOrderItemId == parentOrderItemId).map(item => {

        var output = _.clone(item)
        output.childItems = this.buildNestedItemList(itemList, item.id)
        return output
      })
    },
    wizardStepChanged(tabIndex) {
      // If review selected, rebuild the purchase order list
      if(tabIndex == 2) {
        this.rebuildPurchaseOrderList()
      }
    },
    rebuildPurchaseOrderList() {
      this.supplierPurchaseOrderList = []

      // Collate suppliers across all external items
      let supplierIds = _.uniq(this.itemList.filter(item => item.isExternal && item.supplierContactId != null).map(item => item.supplierContactId))

      this.supplierPurchaseOrderList = supplierIds
      .map(contactId => {
        let supplierInfo = this.supplierLookupList.find(supplier => supplier.value == contactId)
        if(supplierInfo == null)
          return null

        // Build the list for the purchase order
        var externalItems = this.buildPurchaseOrderItemList(contactId)
        let estimatedCost = externalItems.reduce((curCost, item) => {
          if(item.isExternal)
            return curCost + Utils.roundCurrency(item.linePrice)
          return curCost
        }, 0)

        return {
          contactId: contactId,
          name: supplierInfo.name,
          itemList: externalItems,
          estimatedCost: Utils.roundCurrency(estimatedCost)
        }
      })
      .filter(supplierInfo => supplierInfo != null)
    },

    // Build a list of items for a supplier's purchase order
    // We add in parent items of external items even if they aren't external / this supplier
    // So the child items get context & parent details (eg, price group, sizing, etc)
    // For non-external glass items, we'll replace the stock info with 'Customers Own Glass'
    buildPurchaseOrderItemList(supplierContactId) {
      var outputList = []

      // Go through all parent items & work down
      this.itemList.forEach(parentItem => {
        if(parentItem.parentOrderItemId)
          return

        // Drill down & find any external items
        // If none, ignore
        var groupItemList = this.getItemGroupListForItem(this.itemList, parentItem.id)
        if(groupItemList.filter(item => item.isExternal && item.supplierContactId == supplierContactId).length == 0)
          return

        // Ignore any glassService/general items that aren't external
        groupItemList = groupItemList.filter(item => {
          if(item.stockType == InventoryStockTypes.glass || item.stockType == InventoryStockTypes.template)
            return true
          if(item.isExternal && item.supplierContactId == supplierContactId)
            return true
          return false
        })

        // Adjust non-external glass items to customers own
        groupItemList = groupItemList.map(item => {
          var outputItem = _.cloneDeep(item)
          if(item.stockType == InventoryStockTypes.glass && (!item.isExternal || item.supplierContactId != supplierContactId)) {
            outputItem.isCustomerOwnStock = true
          }
          return outputItem
        })


        outputList = _.concat(outputList, groupItemList)
      })

      return outputList
    },

    displayDetailsTab() {
      this.$refs.orderWizard.selectTab(0)
    },
    displayDespatchTab() {
      this.$refs.orderWizard.selectTab(1)
    },
  }

}
</script>